import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Users, PenSquare, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DmChat } from "@/components/groups/GroupChat";
import { useAuth } from "@/context/AuthContext";

const ago = (ts) => (ts ? new Date(ts).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const NewMessage = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/messages/start", { email: email.trim() }); nav(`/messages/${data.thread_id}`); } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={go} data-testid="dm-new-form" className="card p-4 sm:p-5">
      <p className="eyebrow flex items-center gap-2"><PenSquare className="w-4 h-4" />New message</p>
      <div className="mt-3 flex gap-2">
        <input data-testid="dm-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Their Daybreyk email" className="flex-1 min-w-0 h-11 rounded-2xl border border-slate-200 bg-surface-deep/60 px-4 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400" />
        <button data-testid="dm-start-button" disabled={busy || !email.includes("@")} className="inline-flex items-center gap-2 h-11 rounded-2xl bg-gold-400 px-5 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}Start</button>
      </div>
    </form>
  );
};

const Row = ({ to, id, icon: I, title, sub, when, unread }) => (
  <li>
    <Link to={to} data-testid={id} className="card card-hover flex items-center gap-3 !rounded-2xl p-3.5">
      <span className="grid place-items-center w-11 h-11 shrink-0 rounded-full bg-navy-200 text-gold-300">{typeof I === "string" ? <b>{I}</b> : <I className="w-5 h-5" />}</span>
      <span className="flex-1 min-w-0"><span className="flex items-center gap-2"><span className="font-semibold text-white truncate">{title}</span><span className="ml-auto shrink-0 text-[11px] text-slate-500">{when}</span></span>
        <span className="block text-sm text-slate-500 truncate">{sub}</span></span>
      {unread > 0 && <span className="grid place-items-center min-w-6 h-6 px-1.5 rounded-full bg-brand text-xs font-bold text-white">{unread}</span>}
    </Link>
  </li>
);

const Inbox = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/messages/threads").then((r) => setD(r.data ?? { threads: [], groups: [] })).catch(() => setD({ threads: [], groups: [] }));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);
  const threads = Array.isArray(d?.threads) ? d.threads : [];
  const groups = Array.isArray(d?.groups) ? d.groups : [];
  return (
    <main data-testid="messages-page" className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 space-y-6">
      <div><p className="eyebrow">Messages</p><h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-none">All your chats</h1>
        <p className="mt-3 text-slate-600">Direct messages and every group chat in one place.</p></div>
      <NewMessage />
      {d === null && <div className="h-40 rounded-3xl bg-surface animate-pulse" />}
      <section>
        <p className="eyebrow">Direct messages</p>
        {d && !threads.length && <p className="mt-2 text-sm text-slate-500">No direct messages yet.</p>}
        <ul className="mt-2 space-y-2">{threads.map((t) => <Row key={t.thread_id} id={`dm-thread-${t.thread_id}`} to={`/messages/${t.thread_id}`} icon={t.with.slice(0, 1)} title={t.with} sub={t.last ? `${t.last.name}: ${t.last.text}` : "Say hello"} when={ago(t.last?.ts)} unread={t.unread} />)}</ul>
      </section>
      <section>
        <p className="eyebrow">Group chats</p>
        {d && !groups.length && <p className="mt-2 text-sm text-slate-500">You're not in any groups yet. <Link to="/groups" className="font-semibold text-gold-300">Find one</Link></p>}
        <ul className="mt-2 space-y-2">{groups.map((g) => <Row key={g.group_id} id={`dm-group-${g.group_id}`} to={`/groups/${g.group_id}?tab=chat`} icon={Users} title={g.name} sub={g.last ? `${g.last.name}: ${g.last.text || "sent a file"}` : "No messages yet"} when={ago(g.last?.ts)} />)}</ul>
      </section>
    </main>
  );
};

const Thread = ({ tid }) => {
  const [info, setInfo] = useState(null);
  const me = useAuth().user?.user_id;
  useEffect(() => {
    api.get(`/messages/${tid}`).then((r) => setInfo(r.data ?? false)).catch(() => setInfo(false));
  }, [tid]);
  if (info === false) return <main className="mx-auto max-w-3xl px-4 py-12 text-slate-500">Conversation not found. <Link to="/messages" className="text-gold-300 font-semibold">Back</Link></main>;
  return (
    <main data-testid="dm-thread-page" className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10 space-y-4">
      <Link to="/messages" data-testid="dm-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-white"><ArrowLeft className="w-4 h-4" />All messages</Link>
      <h1 data-testid="dm-with" className="font-display text-4xl text-white">{info?.with ?? "…"}</h1>
      {me && <DmChat tid={tid} me={me} />}
    </main>
  );
};

export default function Messages() {
  const { tid } = useParams();
  return tid ? <Thread tid={tid} /> : <Inbox />;
}
